import React, { useState } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td } from '../../ui/Table';
import Button from '../../ui/Button';
import { ArchiveRestore } from 'lucide-react';

const DetailItem = ({ label, value }) => (
  <div className="flex flex-col gap-1 mb-4">
    <span className="font-semibold text-gray-900 text-[12px] uppercase tracking-wider">{label}</span>
    <span className="text-gray-600 text-[13px]">{value || '-'}</span>
  </div>
);

const getPaymentMethod = (method) => {
  if (method === 'CASH') return 'В брой';
  if (method === 'BANK_TRANSFER') return 'По банков път';
  return method;
};

const DeletedInvoiceRow = ({ inv, handleRestoreDeletedInvoice }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <Tr 
        key={inv.id} 
        className={`group cursor-pointer hover:bg-gray-50/50 transition-colors ${isExpanded ? 'bg-gray-50/50' : 'border-b border-gray-50 last:border-0'}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Td className="font-medium text-gray-900">{inv.invoiceNumber}</Td>
        <Td className="max-w-[200px] truncate">{inv.receiverName}</Td>
        <Td>{inv.creationDate}</Td>
        <Td>{inv.totalAmount}</Td>
        <Td onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
            <button onClick={() => handleRestoreDeletedInvoice(inv.id)} className="w-8 h-8 flex items-center justify-center text-tenant-primary hover:text-tenant-hover bg-tenant-light hover:bg-tenant-light rounded-md transition-colors" title="Възстанови">
              <ArchiveRestore size={16} strokeWidth={2} />
            </button>
          </div>
        </Td>
      </Tr>
      
      {isExpanded && (
        <Tr className="border-b border-gray-100 bg-gray-50/30">
          <Td colSpan={5} className="p-0">
            <div className="p-6 overflow-hidden">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
                <DetailItem label="Номер на фактура" value={inv.invoiceNumber} />
                <DetailItem label="Получател" value={inv.receiverName} />
                <DetailItem label="ДДС Номер" value={inv.receiverVatNumber} />
                <DetailItem label="Идентификационен номер" value={inv.receiverIdNumber} />
                
                <DetailItem label="Адрес" value={inv.receiverAddress} />
                <DetailItem label="МОЛ" value={inv.receiverMol} />
                <DetailItem label="Телефон" value={inv.receiverPhoneNumber} />
                <DetailItem label="Обща сума" value={`€ ${inv.totalAmount}`} />
                
                <DetailItem label="Обща сума с думи" value={inv.totalAmountInWords} />
                <DetailItem label="Метод на плащане" value={getPaymentMethod(inv.paymentMethod)} />
                {inv.paymentMethod === 'BANK_TRANSFER' && (
                  <DetailItem label="Банкова сметка" value={inv.iban ? `${inv.bank} - ${inv.iban}` : ''} />
                )}
                <DetailItem label="Описание на сделката" value={inv.dealDescription} />
                
                <DetailItem label="Дата на издаване" value={inv.creationDate ? new Date(inv.creationDate).toLocaleDateString() : ''} />
                <DetailItem label="Дата на падеж" value={inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : ''} />
                <DetailItem label="Дата на данъчно събитие" value={inv.taxEventDate ? new Date(inv.taxEventDate).toLocaleDateString() : ''} />
              </div>

              {inv.items && inv.items.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200/60">
                  <span className="font-semibold text-gray-900 text-[12px] uppercase tracking-wider mb-3 block">Артикули:</span>
                  <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                    <table className="w-full text-left text-[13px]">
                      <thead className="bg-gray-50/80">
                        <tr>
                          <th className="px-4 py-2 font-medium text-gray-500">Име</th>
                          <th className="px-4 py-2 font-medium text-gray-500 text-right w-24">Количество</th>
                          <th className="px-4 py-2 font-medium text-gray-500 text-right w-32">Цена (бр.)</th>
                          <th className="px-4 py-2 font-medium text-gray-500 text-right w-32">Общо</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {inv.items.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50/50">
                            <td className="px-4 py-2 text-gray-900">{item.itemName}</td>
                            <td className="px-4 py-2 text-gray-600 text-right">{item.itemQuantity}</td>
                            <td className="px-4 py-2 text-gray-600 text-right">€ {item.itemPrice}</td>
                            <td className="px-4 py-2 text-gray-900 text-right font-medium">€ {(item.itemQuantity * item.itemPrice).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

const DeletedInvoicesTable = ({
  invoices = [],
  handleRestoreDeletedInvoice,
  loadMore,
  hasMore
}) => {
  return (
    <div className="px-6 pb-6">
      <div className="border border-gray-100 rounded-xl overflow-hidden">
        <Table>
          <Thead className="bg-white">
            <Tr className="border-b border-gray-100 hover:bg-transparent">
              <Th className="text-gray-900 text-[13px] capitalize w-24">Фактура</Th>
              <Th className="text-gray-900 text-[13px] capitalize">Получател</Th>
              <Th className="text-gray-900 text-[13px] capitalize">Създадена</Th>
              <Th className="text-gray-900 text-[13px] capitalize">Стойност</Th>
              <Th className="text-gray-900 text-[13px] capitalize text-right w-24">Действия</Th>
            </Tr>
          </Thead>
          <Tbody>
            {invoices.length === 0 ? (
              <Tr>
                <Td colSpan={5} className="text-center text-gray-500 py-8">Няма изтрити фактури</Td>
              </Tr>
            ) : (
              invoices.map((inv) => (
                <DeletedInvoiceRow 
                  key={inv.id} 
                  inv={inv} 
                  handleRestoreDeletedInvoice={handleRestoreDeletedInvoice} 
                />
              ))
            )}
          </Tbody>
        </Table>
      </div>

      {hasMore && (
        <div className="px-4 py-6 flex justify-center border-t border-gray-100 mt-2">
          <Button onClick={loadMore} variant="primary" className="text-[13px] font-medium w-full sm:w-auto px-8 shadow-sm">
            Зареди още
          </Button>
        </div>
      )}
    </div>
  );
};

export default DeletedInvoicesTable;
